import type { ComponentStatus, UptimeBand } from '@/types';

export type StatusLocale = 'en' | 'pt-BR';

type StatusMessages = {
  platformStatus: string;
  activeIncidents: string;
  incidentHistory: string;
  noRecentIncidents: string;
  startedAt: (date: string) => string;
  statusUnavailable: string;
  staleSnapshot: (relative: string) => string;
  loading: string;
  updated: (relative: string) => string;
  refresh: string;
  refreshing: string;
  viewTicket: string;
  footerRefresh: (relative: string) => string;
  poweredBy: string;
  uptimeAria: (days: number) => string;
  uptimeCellTitle: (date: string, label: string, uptime: string) => string;
  uptimeCellNoData: (date: string) => string;
  uptimeDowntime: (minutes: number) => string;
  uptimeTitle: (days: number) => string;
  uptimeRangeStart: (days: number) => string;
  uptimeToday: string;
  responseTimes: string;
  responseTimesAria: (current: string, peak: string) => string;
  responseTimesNoData: string;
  relativeJustNow: string;
  relativeSecondsAgo: (seconds: number) => string;
  relativeMinutesAgo: (minutes: number) => string;
  relativeHoursAgo: (hours: number) => string;
  relativeDaysAgo: (days: number) => string;
};

type StatusCopy = {
  messages: StatusMessages;
  labels: Record<ComponentStatus, string>;
  headlines: Record<ComponentStatus, string>;
  bands: Record<UptimeBand, string>;
};

export const supportedStatusLocales: StatusLocale[] = ['en', 'pt-BR'];

const statusCopy = {
  en: {
    messages: {
      platformStatus: 'Platform Status',
      activeIncidents: 'Active Incidents',
      incidentHistory: 'Incident History',
      noRecentIncidents: 'No incidents reported in the last 90 days.',
      startedAt: (date: string) => `Started ${date}`,
      statusUnavailable: 'Status Currently Unavailable',
      staleSnapshot: (relative: string) =>
        `Status data is out of date (last collected ${relative}). This region may be paused.`,
      loading: 'Loading...',
      updated: (relative: string) => `Updated ${relative}`,
      refresh: 'Refresh',
      refreshing: 'Refreshing...',
      viewTicket: 'View ticket',
      footerRefresh: (relative: string) =>
        `Status data refreshes every 30 seconds. Last fetch: ${relative}.`,
      poweredBy: 'Powered by Google Cloud Monitoring',
      uptimeAria: (days: number) => `Uptime for the last ${days} days`,
      uptimeCellTitle: (date: string, label: string, uptime: string) =>
        `${date} (UTC) — ${label}, ${uptime} uptime`,
      uptimeCellNoData: (date: string) => `${date} (UTC) — no data collected`,
      uptimeDowntime: (minutes: number) => `~${minutes} min of downtime`,
      uptimeTitle: (days: number) => `Uptime (last ${days} days)`,
      uptimeRangeStart: (days: number) => `${days} days ago`,
      uptimeToday: 'today',
      responseTimes: 'Response times (24h mean)',
      responseTimesAria: (current: string, peak: string) =>
        `Mean response time over the last 24 hours. Latest ${current}, peak ${peak}.`,
      responseTimesNoData: 'No response-time data collected',
      relativeJustNow: 'just now',
      relativeSecondsAgo: (seconds: number) => `${seconds}s ago`,
      relativeMinutesAgo: (minutes: number) => `${minutes}m ago`,
      relativeHoursAgo: (hours: number) => `${hours}h ago`,
      relativeDaysAgo: (days: number) => `${days}d ago`,
    },
    labels: {
      operational: 'Operational',
      degraded: 'Degraded Performance',
      partial_outage: 'Partial Outage',
      major_outage: 'Major Outage',
      maintenance: 'Under Maintenance',
      unknown: 'Status Unknown',
    },
    headlines: {
      operational: 'All Systems Operational',
      degraded: 'Some Systems Are Experiencing Issues',
      partial_outage: 'Partial Service Outage',
      major_outage: 'Major Service Outage',
      maintenance: 'Scheduled Maintenance In Progress',
      unknown: 'Status Currently Unavailable',
    },
    bands: {
      ok: 'Operational',
      nominal: 'Minor Disruption',
      degraded: 'Degraded Performance',
      partial: 'Partial Outage',
      major: 'Major Outage',
      nodata: 'No Data',
    },
  },
  'pt-BR': {
    messages: {
      platformStatus: 'Status da Plataforma',
      activeIncidents: 'Incidentes Ativos',
      incidentHistory: 'Histórico de Incidentes',
      noRecentIncidents: 'Nenhum incidente reportado nos últimos 90 dias.',
      startedAt: (date: string) => `Iniciado em ${date}`,
      statusUnavailable: 'Status Indisponível no Momento',
      staleSnapshot: (relative: string) =>
        `Os dados de status estão desatualizados (última coleta ${relative}). Esta região pode estar pausada.`,
      loading: 'Carregando...',
      updated: (relative: string) => `Atualizado ${relative}`,
      refresh: 'Atualizar',
      refreshing: 'Atualizando...',
      viewTicket: 'Ver ticket',
      footerRefresh: (relative: string) =>
        `Os dados de status são atualizados a cada 30 segundos. Última busca: ${relative}.`,
      poweredBy: 'Fornecido por Google Cloud Monitoring',
      uptimeAria: (days: number) => `Disponibilidade dos últimos ${days} dias`,
      uptimeCellTitle: (date: string, label: string, uptime: string) =>
        `${date} (UTC) — ${label}, ${uptime} de disponibilidade`,
      uptimeCellNoData: (date: string) => `${date} (UTC) — sem dados coletados`,
      uptimeDowntime: (minutes: number) => `~${minutes} min fora do ar`,
      uptimeTitle: (days: number) => `Disponibilidade (últimos ${days} dias)`,
      uptimeRangeStart: (days: number) => `há ${days} dias`,
      uptimeToday: 'hoje',
      responseTimes: 'Tempo de resposta (média 24h)',
      responseTimesAria: (current: string, peak: string) =>
        `Tempo médio de resposta nas últimas 24 horas. Último ${current}, pico ${peak}.`,
      responseTimesNoData: 'Sem dados de tempo de resposta coletados',
      relativeJustNow: 'agora mesmo',
      relativeSecondsAgo: (seconds: number) => `há ${seconds}s`,
      relativeMinutesAgo: (minutes: number) => `há ${minutes}min`,
      relativeHoursAgo: (hours: number) => `há ${hours}h`,
      relativeDaysAgo: (days: number) => `há ${days}d`,
    },
    labels: {
      operational: 'Operacional',
      degraded: 'Desempenho Degradado',
      partial_outage: 'Indisponibilidade Parcial',
      major_outage: 'Indisponibilidade Geral',
      maintenance: 'Em Manutenção',
      unknown: 'Status Desconhecido',
    },
    headlines: {
      operational: 'Todos os Sistemas Operacionais',
      degraded: 'Alguns Sistemas Estão com Instabilidade',
      partial_outage: 'Indisponibilidade Parcial do Serviço',
      major_outage: 'Indisponibilidade Geral do Serviço',
      maintenance: 'Manutenção Programada em Andamento',
      unknown: 'Status Indisponível no Momento',
    },
    bands: {
      ok: 'Operacional',
      nominal: 'Instabilidade Pontual',
      degraded: 'Desempenho Degradado',
      partial: 'Indisponibilidade Parcial',
      major: 'Indisponibilidade Geral',
      nodata: 'Sem Dados',
    },
  },
} satisfies Record<StatusLocale, StatusCopy>;

function normalizeStatusLocale(value: string | null | undefined): StatusLocale | null {
  if (!value) return null;
  const normalized = value.trim().replace('_', '-').toLowerCase();
  if (normalized === 'pt' || normalized.startsWith('pt-br')) return 'pt-BR';
  if (normalized === 'en' || normalized.startsWith('en-')) return 'en';
  return null;
}

function localeFromCookie(): StatusLocale | null {
  if (typeof document === 'undefined') return null;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('xtrakt_locale=') || part.startsWith('cogneris_locale='));
  if (!cookie) return null;
  return normalizeStatusLocale(decodeURIComponent(cookie.split('=').slice(1).join('=')));
}

export function resolveStatusLocale(preferred?: string | readonly string[]): StatusLocale {
  const candidates = Array.isArray(preferred) ? preferred : [preferred];
  for (const candidate of candidates) {
    const locale = normalizeStatusLocale(candidate);
    if (locale) return locale;
  }

  const cookieLocale = localeFromCookie();
  if (cookieLocale) return cookieLocale;

  if (typeof navigator !== 'undefined') {
    const browserCandidates = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const candidate of browserCandidates) {
      const locale = normalizeStatusLocale(candidate);
      if (locale) return locale;
    }
  }

  return 'en';
}

export function getStatusI18n(locale: StatusLocale = statusLocale): StatusCopy {
  return statusCopy[locale] ?? statusCopy.en;
}

export const statusLocale = resolveStatusLocale();
const currentStatusCopy = getStatusI18n(statusLocale);

export const statusMessages = currentStatusCopy.messages;
export const statusLabels = currentStatusCopy.labels;
export const statusHeadlines = currentStatusCopy.headlines;
export const uptimeBandLabels = currentStatusCopy.bands;
